import lowerFirst from 'lodash/lowerFirst';
import merge from 'lodash/merge';

import { NODE_TYPES } from '../../constants';
import { WorkflowState } from '../../ducks';
import { WorkflowType } from '../../enums';
import {
  AddContactNodeData,
  BackendNode,
  BackendNoteNode,
  BackendWFNode,
  BackendWorkflow,
  CallWebhookNodeData,
  ChangeContactFieldNodeData,
  ChannelOptNodeData,
  DelayNodeData,
  FbCustomAudienceNodeData,
  FieldMatchNodeData,
  HoldNodeData,
  HoldUntilReplyNodeData,
  MetricDefinitionsBySendAction,
  Node,
  NodeData,
  NodeDataJson,
  NodeType,
  NoteNode,
  RemoveContactNodeData,
  SendNodeData,
  SendToWorkflowNodeData,
  StartNodeData,
  WFNode,
  Workflow,
  AnyNode,
} from '../../types';
import channelOptUtils from './channelOptUtils';
import { parseOutputConnectors } from './connectorUtils';
import contactUtils from './contactActionUtils';
import fbAudienceUtils from './fbAudienceUtils';
import fieldsMatchUtils from './fieldsMatchUtils';
import {
  doesNodeMatchType,
  getBaseNodeConfig,
  getFixedNodeTitle,
  getNodeTypeFromNode,
} from './helpers';
import miscUtils from './miscUtils';
import modifyJourney from './modifyJourneyUtils';
import { parseBackendNoteId } from './noteUtils';
import sendNodeUtils from './sendNodeUtils';
import startUtils from './startNodeUtils';
import subscriptionUtils from './subscriptionUtils';
import { validateNode } from './validationUtils';
import { WorkflowView } from 'modules/workflows/constants/workflow';

interface ParseNodeOptions {
  /** The start node of the workflow.  Only needed for validation purposes */
  startNode?: Node | null;
  /** Whether or not validation should be run on the node */
  shouldValidate?: boolean;
}

type WFStateForParsing = Pick<
  WorkflowState,
  | 'isNotesVisible'
  | 'isQuietHoursFeatureEnabled'
  | 'isYesNoSplitDataToggleEnabled'
  | 'isMetricsSelectionEnabled'
  | 'isInProgressTotalPublishdViewEnabled'
  | 'nodes'
  | 'projectTimeZone'
  | 'workflowView'
> & { workflow: WorkflowState['workflow'] | BackendWorkflow };

export const hydrateJourneyVisitorCountState = (
  isInProgressTotalPublishedView: boolean,
  workflowView?: WorkflowView,
  workflowType?: WorkflowType,
) => {
  if (workflowType === WorkflowType.DRAFT) {
    return {
      showVisitorCounts: false,
      showNumUsersCount: true,
      isInProgressTotalPublishedView,
    };
  } else {
    const showVisitorCounts =
      !!isInProgressTotalPublishedView &&
      workflowView === WorkflowView.Total &&
      workflowType === WorkflowType.PUBLISHED;

    return {
      showNumUsersCount: !showVisitorCounts,
      showVisitorCounts,
      isInProgressTotalPublishedView,
    };
  }
};

/**
 * Parses the node data for a node so that it appears correctly in the
 * flowchart.
 *
 * TODO: Change the incoming args to a single object for better readability
 *
 * @param nodeData The node data to parse
 * @param nodeType The type of node to parse
 * @param isQuietHoursFeatureEnabled Whether the quiet hours feature is enabled
 * @returns The parsed node data
 */
const parseNodeData = ({
  nodeData = {},
  nodeType,
  isQuietHoursFeatureEnabled = false,
  isMetricsSelectionEnabled = false,
  selectedMetrics,
  projectTimeZone,
}: {
  nodeData: Partial<NodeData>;
  nodeType: NodeType;
  isQuietHoursFeatureEnabled?: boolean;
  isMetricsSelectionEnabled?: boolean;
  selectedMetrics?: MetricDefinitionsBySendAction;
  projectTimeZone?: string;
}): Partial<NodeData> => {
  const updatedData: Partial<NodeData> = {};

  switch (nodeType) {
    case NODE_TYPES.START:
      updatedData.textSegments = startUtils.getTextSegments(nodeData as StartNodeData);
      break;

    /* ----------------------------- CONTACT ACTION ----------------------------- */

    case NODE_TYPES.ADD_CONTACT:
      updatedData.textSegments = contactUtils.addContact.getDescription(
        nodeData as AddContactNodeData,
      );
      break;

    case NODE_TYPES.UPDATE_CONTACT_WEBHOOK:
    case NODE_TYPES.CHANGE_CONTACT_FIELD:
      updatedData.textSegments = contactUtils.changeField.getTextSegments(
        nodeData as ChangeContactFieldNodeData,
      );
      break;

    case NODE_TYPES.REMOVE_CONTACT:
      updatedData.textSegments = contactUtils.removeContact.getDescription(
        nodeData as RemoveContactNodeData,
      );
      break;

    /* ------------------------------- MISC ACTION ------------------------------ */

    case NODE_TYPES.CALL_WEBHOOK:
      updatedData.textSegments = miscUtils.webhook.getTextSegments(nodeData as CallWebhookNodeData);
      break;

    case NODE_TYPES.TRIGGER_WORKFLOW:
      updatedData.textSegments = miscUtils.workflow.getTextSegments(
        nodeData as SendToWorkflowNodeData,
      );
      break;

    /* ----------------------------- MODIFY JOURNEY ----------------------------- */

    case NODE_TYPES.ATTRIBUTE_SPLIT:
      updatedData.textSegments = modifyJourney.attributeSplit.getTextSegments(nodeData);
      break;

    case NODE_TYPES.DELAY:
      updatedData.textSegments = modifyJourney.delay.getTextSegments(nodeData as DelayNodeData);
      if (nodeData?.delayUsingProperty?.dataType) {
        updatedData.delayUsingProperty = {
          ...nodeData.delayUsingProperty,
          // Although `dataType` is being retrieved from the backend as PascalCase,
          // saving fails unless its camelCase
          dataType: lowerFirst(nodeData?.delayUsingProperty?.dataType),
        };
      }
      break;

    case NODE_TYPES.HOLD:
      updatedData.textSegments = modifyJourney.hold.getTextSegments(nodeData as HoldNodeData);
      break;
    case NODE_TYPES.HOLD_UNTIL_REPLY:
      updatedData.textSegments = modifyJourney.holdUntilReply.getTextSegments(
        nodeData as HoldUntilReplyNodeData,
      );
      break;
    case NODE_TYPES.FIELDS_MATCH:
      updatedData.textSegments = fieldsMatchUtils.getTextSegments(nodeData as FieldMatchNodeData);
      break;

    /* ------------------------------- SEND ACTION ------------------------------ */

    case NODE_TYPES.SEND_EMAIL:
    case NODE_TYPES.SEND_PUSH:
    case NODE_TYPES.SEND_SMS:
    case NODE_TYPES.SEND_WEB_PUSH:
    case NODE_TYPES.SEND_WHATSAPP:
    case NODE_TYPES.SEND_EMBEDDED:
    case NODE_TYPES.SEND_IN_APP:
      updatedData.textSegments = sendNodeUtils.getTextSegments(nodeData as SendNodeData, nodeType, {
        isQuietHoursFeatureEnabled,
        isMetricsSelectionEnabled,
        selectedMetrics,
        projectTimeZone,
      });
      break;

    /* ------------------------------- SUBSCRIPTIONS ------------------------------ */

    case NODE_TYPES.MODIFY_CHANNEL_SUBSCRIPTION:
    case NODE_TYPES.MODIFY_MESSAGE_TYPE:
      updatedData.textSegments = subscriptionUtils.modifySubscription.getDescription(nodeData);
      break;

    /* ------------------------------- FB AUDIENCE ------------------------------ */

    case NODE_TYPES.ADD_USER_TO_FB:
    case NODE_TYPES.REMOVE_USER_FROM_FB:
      updatedData.textSegments = fbAudienceUtils.getTextSegments(
        nodeData as FbCustomAudienceNodeData,
      );
      break;

    /* ------------------------------- CHANNEL OPTIMIZATION ----------------------*/

    case NODE_TYPES.CHANNEL_OPT:
      updatedData.childNodes = channelOptUtils.parseChildNodes(
        { isQuietHoursFeatureEnabled, isMetricsSelectionEnabled, selectedMetrics },
        nodeData.childNodes,
      );
      updatedData.nodeDescBottom = channelOptUtils.getDescription(
        updatedData as ChannelOptNodeData,
      );
      break;

    /* ---------------------------------------------------------------------------- */
    default:
      break;
  }

  return {
    ...nodeData,
    ...updatedData,
  };
};

interface ParseWorkflowNodeOptions extends WFStateForParsing, ParseNodeOptions {
  nodeDefaults: Partial<Node>;
}

/**
 * Parses a workflow node to be used in the flowchart.
 * @returns The parsed workflow node
 */
const parseWorkflowNode = (node: AnyNode, options: ParseWorkflowNodeOptions) => {
  const {
    isMetricsSelectionEnabled,
    isQuietHoursFeatureEnabled,
    isYesNoSplitDataToggleEnabled,
    nodeDefaults,
    shouldValidate = false,
    workflow,
    nodes = {},
    projectTimeZone,
  } = options;
  const startNode = options.startNode ?? nodes[workflow?.startWorkflowNodeId];
  const { type } = node;
  // apply additional nodeData attributes from pre-configured node configs
  // and backfill the required fields until they're available on GraphQL
  const nodeData = parseNodeData({
    nodeData: {
      // default data
      ...(nodeDefaults.nodeData || {}),
      // anything that is actually set in the current `nodeData`
      ...node.nodeData,
    },
    nodeType: type as string,
    isQuietHoursFeatureEnabled,
    isMetricsSelectionEnabled,
    selectedMetrics: workflow?.tileMetrics,
    projectTimeZone,
  });

  const isStartNode = type === NODE_TYPES.START;
  const isPublished = workflow?.workflowType === WorkflowType.PUBLISHED;
  const shouldCheckForErrors = !!(
    shouldValidate &&
    isYesNoSplitDataToggleEnabled &&
    startNode &&
    type === NODE_TYPES.FIELDS_MATCH
  );

  return {
    ...node,
    ...(shouldCheckForErrors && validateNode(node, { startNode })),
    // Published nodes should not be deletable
    canDelete: !isPublished && !isStartNode,
    canClone: !isStartNode,
    outputConnectors: parseOutputConnectors(nodeData, type as string),
    // there shouldn't be any input connectors to start nodes as this can
    // result in an infinitely looping workflow
    inputConnectors: isStartNode ? [] : [{ name: '' }],
    nodeData,
  };
};

/**
 * Parses a node node for use in the flowchart.
 * @returns The parsed node node
 */
const parseNoteNode = (
  node: Node,
  { isNotesVisible, workflow }: Partial<WFStateForParsing> = {},
) => {
  // const isNew = parseNodeIdToInt(node.id) < 0;
  // If it is not feature enabled, always hide the note
  // otherwise check the stored value for whether the note is visible
  const isPublished = workflow?.workflowType === WorkflowType.PUBLISHED;

  /* Temporarily disable isLinkButtonVisible until sharing is fixed */
  return {
    ...node,
    canDelete: !isPublished,
    canClone: true,
    isHidden: !isNotesVisible,
    isLinkButtonVisible: false,
    showFullTextAsTooltip: false,
  };
};

/**
 * Parses the node so that it has the correct text and formatting in the
 * flowchart.
 *
 * It uses the information (mostly found in `nodeData`) to determine what should
 * show, and updates the node accordingly.
 *
 * REVIEW: See if this is how we should set colors and other data in WF-468
 */
export const parseNode = (
  rawNode: AnyNode,
  wfState: WFStateForParsing,
  options: ParseNodeOptions = {},
): Node => {
  const { workflow = {} } = wfState || {};
  const isInProgressTotalPublishedView = wfState?.isInProgressTotalPublishdViewEnabled;
  const workflowView = wfState?.workflowView;
  const workflowType = (workflow as Workflow)?.workflowType;
  const journeyVisitorCountsState = hydrateJourneyVisitorCountState(
    isInProgressTotalPublishedView,
    workflowView,
    workflowType,
  );

  const type = getNodeTypeFromNode(rawNode);
  const { data: nodeDefaults = {} } = getBaseNodeConfig(type) || {};
  const node = {
    ...rawNode,
    // G6 - used for the flowchart - requires the id to be a string
    // See: https://github.com/antvis/G6/issues/1239
    id: `${rawNode.id}`,
    defaultTitle: (nodeDefaults as Node)?.defaultTitle,
    type,
    isSelected: Boolean(rawNode.isSelected),
    workflowId: (workflow as Workflow)?.id,
    ...journeyVisitorCountsState,
  };

  if (rawNode.isNote) {
    return parseNoteNode(node as NoteNode, wfState) as Node;
  }

  return parseWorkflowNode(node, {
    nodeDefaults,
    ...(wfState || {}),
    ...options,
  }) as unknown as Node;
};

/**
 * Removes fields from `nodeDataJson` that we do not want to overwrite.
 *
 * Reasons for removal:
 *  - `channels`:  Returns as object in `nodeDataJson`, which does not respect
 *    order.  Instead we use the array returned in `nodeData`
 */

const removeConflictingNodeDataJsonFields = ({ channels, ...rest }: NodeDataJson) => rest;

/**
 * `LooseBackendNode` is used when a backend node has been partially parsed.  It
 * allows for the id to be a number or a string.
 */
interface LooseBackendNode extends Omit<BackendWFNode, 'id'>, Omit<BackendNoteNode, 'id'> {
  id: BackendNode['id'] | Node['id'];
}

/**
 * Merges `nodeData` with `nodeDataJson` returned from server
 */
const mergeNodeDataWithJson = ({
  nodeData = {},
  nodeDataJson = {},
  resource,
}: LooseBackendNode): NodeData => ({
  /**
   * Overwrites `nodeData` with the following fields from `nodeDataJson`:
   * - nodeClass
   * - nodeSuperClass
   * - dataType:
   *    - The dataType returned by gql is not the same as the dataType expected
   *      in our app.
   *    - See the following for more details:
   *      - https://iterable.slack.com/archives/CN6649E9L/p1619121122437500
   *      - https://iterable.atlassian.net/browse/WF-1116
   */
  ...merge({}, nodeData, removeConflictingNodeDataJsonFields(nodeDataJson)),
  // Weird workaround since we always update `nodeData` instead of `node`
  resource,
});

const isChannelOptNode = (nodeData: Partial<NodeData>): nodeData is ChannelOptNodeData =>
  doesNodeMatchType(NODE_TYPES.CHANNEL_OPT, { nodeData });

/**
 * Parses node data from nodes initially returned from server
 */
const parseBackendNodeData = (node: LooseBackendNode): NodeData => {
  const { resource } = node;
  const nodeData = mergeNodeDataWithJson(node);

  return isChannelOptNode(nodeData)
    ? {
        ...nodeData,
        childNodes: channelOptUtils.constructChildNodes(nodeData.channels, resource),
      }
    : nodeData;
};

/**
 * Parses the nodes that are returned from the server.
 *
 * IMPORTANT: This should only be called on each node once, when they are first
 * retrieved from the server.
 *
 * NOTE: you have to add `isNote` to the node correct the id before calling this
 * @param node The node to parse
 * @param wfState The workflow state
 * @param options Options which affect how the node is parsed
 * @returns The parsed node
 */
export const parseBackendNode = (
  node: LooseBackendNode,
  wfState: WFStateForParsing,
  options: ParseNodeOptions,
) => {
  const { workflow = {} } = wfState || {};
  const workflowView = wfState?.workflowView;
  const isInProgressTotalPublishedView = wfState?.isInProgressTotalPublishdViewEnabled;
  const workflowType = (workflow as Workflow)?.workflowType;

  const journeyVisitorCountsState = hydrateJourneyVisitorCountState(
    isInProgressTotalPublishedView,
    workflowView,
    workflowType,
  );
  const unusedParams = ['nodeDataJson', '__typename'];
  const normalizedNode = Object.entries(node).reduce(
    (acc, [key, value]) => (unusedParams.includes(key) ? acc : { ...acc, [key]: value }),
    {},
  );

  const updatedNode = node.isNote
    ? (normalizedNode as NoteNode)
    : ({
        ...normalizedNode,
        ...journeyVisitorCountsState,
        nodeData: {
          ...parseBackendNodeData(node),
        },
      } as Omit<WFNode, 'type'>);

  updatedNode.type = getNodeTypeFromNode(updatedNode) as NodeType;
  /**
   * Removes default node titles which had accidentally been saved to server.
   * See https://iterable.slack.com/archives/CN6649E9L/p1643407505487339 for details.
   */
  updatedNode.title = getFixedNodeTitle(updatedNode.title, updatedNode.type);

  return parseNode(updatedNode as Node, wfState, options);
};

/**
 * Parses the nodes returned from the backend so that they are in a format that
 * frontend can use
 * @param args.nodes The nodes to parse
 * @param args.notes The notes to parse
 * @param args.shouldValidate Whether or not to validate the nodes
 * @param args.startWorkflowNodeId The id of the start node
 * @param wfState The workflow state
 * @returns A version of the nodes that is readable by the frontend
 */
export const parseBackendNodes = (
  {
    nodes = [],
    notes = [],
    shouldValidate = false,
    startWorkflowNodeId,
  }: {
    nodes?: BackendWFNode[];
    notes?: BackendNoteNode[];
    shouldValidate?: boolean;
    startWorkflowNodeId?: number;
  },
  wfState: WFStateForParsing,
) => {
  const startId = startWorkflowNodeId || wfState?.workflow?.startWorkflowNodeId;
  const rawStartNode = nodes.find((node) => `${node.id}` === `${startId}`);
  const startNode = rawStartNode
    ? parseBackendNode(rawStartNode, wfState, { shouldValidate })
    : null;
  const options = { startNode, shouldValidate };
  const parsedNodes = nodes.reduce(
    (acc: Node[], node: BackendNode & { [key: string]: any }) =>
      `${node.id}` === `${startId}`
        ? [...acc, startNode as Node]
        : [...acc, parseBackendNode({ ...node, isNote: false }, wfState, options)],
    [],
  );
  const parsedNotes = notes.map((note) =>
    parseBackendNode({ ...note, isNote: true, id: parseBackendNoteId(note.id) }, wfState, options),
  );

  return [...parsedNodes, ...parsedNotes];
};
